import { useState } from 'react';
import {
  Box,
  Divider,
  Drawer,
  IconButton,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Tooltip,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import SpaceDashboardIcon from '@mui/icons-material/SpaceDashboard';
import PeopleIcon from '@mui/icons-material/People';
import SwitchAccountIcon from '@mui/icons-material/SwitchAccount';
import StopCircleIcon from '@mui/icons-material/StopCircle';
import LogoutIcon from '@mui/icons-material/Logout';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import AdminPanelSettingsIcon from '@mui/icons-material/AdminPanelSettings';
import ExpandLess from '@mui/icons-material/ExpandLess';
import ExpandMore from '@mui/icons-material/ExpandMore';
import Collapse from '@mui/material/Collapse';
import { useNavigate, useLocation } from 'react-router-dom';
import { useIsAuthenticated, useMsal } from '@azure/msal-react';
import { useQueryClient } from '@tanstack/react-query';

import { isAuthEnabled } from '@/auth';
import { GlobalRole } from '@/auth/permissions.types';
import { broadcastLogout } from '@/hooks/useCrossTabLogout';
import { useHomeNavigation } from '@/hooks/useHomeNavigation';
import { paths } from '@/routes/paths';
import { useAuthStore } from '@/stores/useAuthStore';
import { useCanImpersonate } from '@/queries/useImpersonation';
import { useSidebarStore } from '@/stores/useSidebarStore';
import permissionsApi from '@/api/permissionsApi';
import logoLight from '@/assets/images/logo-light.svg';
import logoIcon from '@/assets/images/logo-icon.svg';
import pathwayIcon from '@/assets/images/pathway-icon.png';
import { SIDEBAR_COLLAPSED_WIDTH, SIDEBAR_EXPANDED_WIDTH } from '@/theme/theme';

interface NavItem {
  label: string;
  path?: string;
  icon?: React.ReactNode;
  children?: { label: string; path: string }[];
}

const staffingActivitiesChildren = [
  { label: 'OC&Cer Search', path: paths.staffingActivities.occerSearch },
  { label: 'Job End Admin', path: paths.staffingActivities.jobEndAdmin },
  { label: 'Talent Committees', path: paths.staffingActivities.talentCommittees },
  { label: 'Talent Committee Run', path: paths.staffingActivities.talentCommitteeRun },
  { label: 'Calibration Matrix', path: paths.staffingActivities.calibrationMatrix },
  { label: 'Appraisal Admin', path: paths.staffingActivities.appraisalAdmin },
];

const adminChildren = [
  { label: 'Desktop Notifications Admin', path: paths.admin.desktopNotifications },
  { label: 'Notification Templates', path: paths.admin.notificationTemplates },
  { label: 'Skills Matrix', path: paths.admin.skillsMatrix },
  // Admin-only Manual JE Generation page (WI #1103) — distinct from the Staffing Activities
  // "Job End Admin" entry, which opens the Active Job Ends view.
  { label: 'Manual JE Generation', path: paths.admin.manualJobEnd },
];

const navItems: NavItem[] = [
  { label: 'Dashboard', path: paths.dashboard, icon: <SpaceDashboardIcon /> },
  { label: 'Staffing Activities', icon: <PeopleIcon />, children: staffingActivitiesChildren },
  { label: 'Admin', icon: <AdminPanelSettingsIcon />, children: adminChildren },
];

export function Sidebar() {
  const [openSections, setOpenSections] = useState<Record<string, boolean>>({});
  const [isHeaderHovered, setIsHeaderHovered] = useState(false);
  const [pathwayIconError, setPathwayIconError] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const { instance } = useMsal();
  const isAuthenticated = useIsAuthenticated();
  const authEnabled = isAuthEnabled();
  const queryClient = useQueryClient();
  const clearPermissions = useAuthStore((state) => state.clearPermissions);
  const permissions = useAuthStore((state) => state.permissions);
  const globalRoles = permissions?.globalRoles ?? [];
  const hasAnyRole = (roles: GlobalRole[]) => roles.some((role) => globalRoles.includes(role));
  const { collapsed, toggle, mobileOpen, setMobileOpen } = useSidebarStore();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const { data: canImpersonate } = useCanImpersonate();
  const impersonatedUser = useAuthStore((state) => state.impersonatedUser);
  const setImpersonatedUser = useAuthStore((state) => state.setImpersonatedUser);
  const setPermissions = useAuthStore((state) => state.setPermissions);
  const { navigateToHome } = useHomeNavigation();

  const handleStopImpersonation = async (e: React.MouseEvent) => {
    e.stopPropagation();
    setImpersonatedUser(null);
    const newPermissions = await permissionsApi.getMyPermissions();
    setPermissions(newPermissions);
    // Navigate BEFORE clearing cache to avoid 403 errors on current page refetch
    navigateToHome(newPermissions?.globalRoles);
    queryClient.clear();
  };

  const handleNavClick = (path: string) => {
    navigate(path);
    if (isMobile) {
      setMobileOpen(false);
    }
  };

  const handleLogoClick = () => {
    navigateToHome(globalRoles);
    if (isMobile) {
      setMobileOpen(false);
    }
  };

  const handleLogout = () => {
    broadcastLogout();
    clearPermissions();
    queryClient.clear();
    instance.logoutRedirect();
  };

  const isActive = (path?: string) => {
    if (!path) return false;
    return location.pathname === path;
  };

  const isSectionActive = (label: string) => {
    if (label === 'Staffing Activities') {
      return location.pathname.startsWith('/staffing-activities');
    }
    if (label === 'Admin') {
      return location.pathname.startsWith('/admin');
    }
    return false;
  };

  const toggleSection = (label: string) => {
    setOpenSections((prev) => ({ ...prev, [label]: !prev[label] }));
  };

  const sidebarWidth = collapsed ? SIDEBAR_COLLAPSED_WIDTH : SIDEBAR_EXPANDED_WIDTH;

  const isHROrStaffing = hasAnyRole(['HR', 'Staffing']);
  const visibleNavItems = navItems.filter((item) => {
    if (item.label === 'Dashboard') {
      return !isHROrStaffing;
    }
    if (item.label === 'Staffing Activities') {
      return isHROrStaffing;
    }
    if (item.label === 'Admin') {
      return globalRoles.includes('Admin');
    }
    return true;
  });

  const drawerContent = (
      <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
        <Box
          onMouseEnter={() => setIsHeaderHovered(true)}
          onMouseLeave={() => setIsHeaderHovered(false)}
          sx={{
            p: 2,
            display: 'flex',
            alignItems: 'center',
            justifyContent: (collapsed && !isMobile) ? 'center' : 'space-between',
            minHeight: (collapsed && !isMobile) ? 96 : 64,
          }}
        >
          {(collapsed && !isMobile) ? (
            <>
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  gap: 0.5,
                  opacity: isHeaderHovered ? 0 : 1,
                  transition: 'opacity 0.2s',
                }}
              >
                <Box
                  component="img"
                  src={pathwayIcon}
                  alt="Pathway Logo"
                  sx={{
                    height: 28,
                    cursor: 'pointer',
                  }}
                  onClick={handleLogoClick}
                  onError={() => setPathwayIconError(true)}
                />
                <Box
                  component="img"
                  src={logoIcon}
                  alt="OC&C Logo"
                  sx={{
                    height: 28,
                    width: 28,
                    cursor: 'pointer',
                  }}
                  onClick={handleLogoClick}
                />
              </Box>
              <IconButton
                onClick={toggle}
                size="small"
                aria-label="expand sidebar"
                sx={{
                  position: 'absolute',
                  opacity: isHeaderHovered ? 1 : 0,
                  transition: 'opacity 0.2s',
                }}
              >
                <ChevronRightIcon />
              </IconButton>
            </>
          ) : (
            <>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                {pathwayIconError ? (
                  <Typography
                    variant="h6"
                    sx={{ cursor: 'pointer', minHeight: 32 }}
                    onClick={handleLogoClick}
                  >
                    OC&C Pathway
                  </Typography>
                ) : (
                  <>
                    <Box
                      component="img"
                      src={pathwayIcon}
                      alt="Pathway Logo"
                      sx={{ height: 32, cursor: 'pointer' }}
                      onClick={handleLogoClick}
                      onError={() => setPathwayIconError(true)}
                    />
                    <Box
                      component="img"
                      src={logoLight}
                      alt="OC&C Logo"
                      sx={{ height: 32, cursor: 'pointer' }}
                      onClick={handleLogoClick}
                    />
                  </>
                )}
              </Box>
              {!isMobile && (
                <IconButton onClick={toggle} size="small" aria-label="collapse sidebar">
                  <ChevronLeftIcon />
                </IconButton>
              )}
            </>
          )}
        </Box>

        <Divider />

        <List sx={{ flexGrow: 1 }}>
          {visibleNavItems.map((item) => {
            if (item.children) {
              const isOpen = !!openSections[item.label];
              return (
                <Box key={item.label}>
                  <ListItem disablePadding>
                    {(collapsed && !isMobile) ? (
                      <Tooltip title={item.label} placement="right">
                        <ListItemButton
                          selected={isSectionActive(item.label)}
                          sx={{ justifyContent: 'center', px: 2 }}
                        >
                          <ListItemIcon sx={{ minWidth: 0 }}>{item.icon}</ListItemIcon>
                        </ListItemButton>
                      </Tooltip>
                    ) : (
                      <ListItemButton
                        onClick={() => toggleSection(item.label)}
                        selected={isSectionActive(item.label)}
                      >
                        <ListItemIcon sx={{ minWidth: 40 }}>{item.icon}</ListItemIcon>
                        <ListItemText primary={item.label} />
                        {isOpen ? <ExpandLess /> : <ExpandMore />}
                      </ListItemButton>
                    )}
                  </ListItem>
                  {(!collapsed || isMobile) && (
                    <Collapse in={isOpen} timeout="auto" unmountOnExit>
                      <List component="div" disablePadding>
                        {item.children.map((child) => (
                          <ListItem key={child.path} disablePadding>
                            <ListItemButton
                              sx={{ pl: 7 }}
                              onClick={() => handleNavClick(child.path)}
                              selected={location.pathname === child.path}
                            >
                              <ListItemText primary={child.label} />
                            </ListItemButton>
                          </ListItem>
                        ))}
                      </List>
                    </Collapse>
                  )}
                </Box>
              );
            }
            return (
              <ListItem key={item.path} disablePadding>
                {(collapsed && !isMobile) ? (
                  <Tooltip title={item.label} placement="right">
                    <ListItemButton
                      onClick={() => handleNavClick(item.path!)}
                      selected={isActive(item.path)}
                      sx={{ justifyContent: 'center', px: 2 }}
                    >
                      <ListItemIcon sx={{ minWidth: 0 }}>{item.icon}</ListItemIcon>
                    </ListItemButton>
                  </Tooltip>
                ) : (
                  <ListItemButton
                    onClick={() => handleNavClick(item.path!)}
                    selected={isActive(item.path)}
                  >
                    <ListItemIcon sx={{ minWidth: 40 }}>{item.icon}</ListItemIcon>
                    <ListItemText primary={item.label} />
                  </ListItemButton>
                )}
              </ListItem>
            );
          })}
        </List>

        <Divider />
        <List>
          {canImpersonate && (
            <ListItem disablePadding>
              {(collapsed && !isMobile) ? (
                <Tooltip
                  title={impersonatedUser ? `Impersonating: ${impersonatedUser.name}${permissions?.grade ? ` (${permissions.grade}${globalRoles.length > 0 ? `, ${globalRoles.join(', ')}` : ''})` : ''}` : 'Impersonate'}
                  placement="right"
                >
                  <ListItemButton
                    onClick={impersonatedUser ? handleStopImpersonation : () => handleNavClick(paths.admin.impersonate)}
                    selected={isActive(paths.admin.impersonate) || !!impersonatedUser}
                    sx={{
                      justifyContent: 'center',
                      px: 2,
                      ...(impersonatedUser && {
                        bgcolor: 'warning.light',
                        '&:hover': { bgcolor: 'warning.main' },
                      }),
                    }}
                  >
                    <ListItemIcon sx={{ minWidth: 0 }}>
                      {impersonatedUser ? (
                        <StopCircleIcon color="warning" />
                      ) : (
                        <SwitchAccountIcon />
                      )}
                    </ListItemIcon>
                  </ListItemButton>
                </Tooltip>
              ) : (
                <ListItemButton
                  onClick={impersonatedUser ? undefined : () => handleNavClick(paths.admin.impersonate)}
                  selected={isActive(paths.admin.impersonate) || !!impersonatedUser}
                  sx={{
                    flexDirection: impersonatedUser ? 'column' : 'row',
                    alignItems: impersonatedUser ? 'flex-start' : 'center',
                    py: impersonatedUser ? 1.5 : 1,
                    ...(impersonatedUser && {
                      bgcolor: 'warning.light',
                      '&:hover': { bgcolor: 'warning.main' },
                      cursor: 'default',
                    }),
                  }}
                >
                  {impersonatedUser ? (
                    <Box sx={{ width: '100%' }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', mb: 0.5 }}>
                        <ListItemIcon sx={{ minWidth: 40 }}>
                          <SwitchAccountIcon color="warning" />
                        </ListItemIcon>
                        <Typography variant="body2" fontWeight="bold" color="warning.dark">
                          Impersonating
                        </Typography>
                      </Box>
                      <Typography variant="body2" sx={{ pl: 5, color: 'text.secondary' }} noWrap>
                        {impersonatedUser.name}
                      </Typography>
                      <Typography variant="body2" sx={{ pl: 5, color: 'text.secondary', fontSize: '0.75rem' }}>
                        {[permissions?.grade, ...(globalRoles.length > 0 ? [globalRoles.join(', ')] : [])].filter(Boolean).join(' | ')}
                      </Typography>
                      <Box
                        component="span"
                        onClick={handleStopImpersonation}
                        sx={{
                          pl: 5,
                          mt: 0.5,
                          display: 'block',
                          color: 'error.main',
                          fontSize: '0.75rem',
                          cursor: 'pointer',
                          '&:hover': { textDecoration: 'underline' },
                        }}
                      >
                        Stop Impersonation
                      </Box>
                    </Box>
                  ) : (
                    <>
                      <ListItemIcon sx={{ minWidth: 40 }}>
                        <SwitchAccountIcon />
                      </ListItemIcon>
                      <ListItemText primary="Impersonate" />
                    </>
                  )}
                </ListItemButton>
              )}
            </ListItem>
          )}
          {authEnabled && isAuthenticated && (
            <ListItem disablePadding>
              {(collapsed && !isMobile) ? (
                <Tooltip title="Log out" placement="right">
                  <ListItemButton
                    onClick={handleLogout}
                    sx={{ justifyContent: 'center', px: 2 }}
                  >
                    <ListItemIcon sx={{ minWidth: 0 }}>
                      <LogoutIcon />
                    </ListItemIcon>
                  </ListItemButton>
                </Tooltip>
              ) : (
                <ListItemButton onClick={handleLogout}>
                  <ListItemIcon sx={{ minWidth: 40 }}>
                    <LogoutIcon />
                  </ListItemIcon>
                  <ListItemText primary="Log out" />
                </ListItemButton>
              )}
            </ListItem>
          )}
        </List>
      </Box>
  );

  return (
    <>
      {isMobile ? (
        <Drawer
          variant="temporary"
          open={mobileOpen}
          onClose={() => setMobileOpen(false)}
          ModalProps={{
            keepMounted: true, // Better open performance on mobile
          }}
          sx={{
            display: { xs: 'block', md: 'none' },
            '& .MuiDrawer-paper': {
              width: SIDEBAR_EXPANDED_WIDTH,
              boxSizing: 'border-box',
            },
          }}
        >
          {drawerContent}
        </Drawer>
      ) : (
        /* Desktop drawer */
        <Drawer
          variant="permanent"
          sx={{
            display: { xs: 'none', md: 'block' },
            width: sidebarWidth,
            flexShrink: 0,
            '& .MuiDrawer-paper': {
              width: sidebarWidth,
              boxSizing: 'border-box',
              overflowX: 'hidden',
            },
          }}
        >
          {drawerContent}
        </Drawer>
      )}
    </>
  );
}

export default Sidebar;
